import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ResponsiveButton from "../ResponsiveButton/ResponsiveButton";

const defaultPreferences = {
  necessary: true,      // Pakolliset evästeet aina päällä
  preferences: false,   // Toiminnalliset evästeet
  analytics: true,     // Analytiikkaevästeet
  performance: false,   // Suorituskykyevästeet
  marketing: false,     // Markkinointi-/mainosevästeet
  social: false,        // Sosiaalisen median evästeet
};

const CookieConsent = () => {
  const [checked, setChecked] = useState(false);   // Ollaanko localStoragen tilaa tarkistettu
  const [visible, setVisible] = useState(false);   // Näkyykö banneri
  const [preferences, setPreferences] = useState(defaultPreferences);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem("cookieConsent");
    setChecked(true);
    if (!consent) {
     loadAnalytics();     // analytiikka päälle heti ensimmäisellä käynnillä
      setVisible(true);
    } else {
      const parsed = JSON.parse(consent);
      setPreferences(parsed);
      if (parsed.analytics) loadAnalytics();
    }
  }, []);
  
  // Tallennus ja skriptien lataus
  const savePreferences = (prefs) => {
    localStorage.setItem("cookieConsent", JSON.stringify(prefs));
    setVisible(false);
    if (prefs.analytics) loadAnalytics();
    // Voit lisätä esim. if (prefs.marketing) loadMarketing();
  };

  // Google Analytics -skriptin lataus
  const loadAnalytics = () => {
    const script = document.createElement("script");
    script.async = true;
    script.src = "https://www.googletagmanager.com/gtag/js?id=G-EW62ZV3B16";
    document.head.appendChild(script);
    script.onload = () => {
      window.dataLayer = window.dataLayer || [];
      function gtag() { window.dataLayer.push(arguments); }
      window.gtag = gtag;
      gtag("js", new Date());
      gtag("config", "G-EW62ZV3B16");
    };
  };

  if (!checked) return null; // Ei renderöidä ennen localStorage-tarkistusta

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="fixed bottom-0 left-0 w-full z-50 bg-linkcol dark:bg-purple-800 p-4 shadow-lg dark:bg-opacity-90"
        >
          <p className="text-center font-customFont1 dark:text-primary text-darkest mb-4 text-sm sm:text-base leading-relaxed max-w-4xl mx-auto">
            Käytämme evästeitä parantaaksemme käyttökokemustasi ja seuratakseemme sivuston käyttöä.
            Valitse, mitkä evästetyypit haluat sallia:
          </p>

          {/* Pika-valinnat */}
          <div className="flex flex-wrap justify-center gap-4 mb-4">
            <ResponsiveButton
              variant="addtocart"
              onClick={() => savePreferences({ ...defaultPreferences, preferences: true, analytics: true, performance: true, marketing: true, social: true })}
            >
              Hyväksy kaikki
            </ResponsiveButton>
            <ResponsiveButton
              variant="removefromcart"
              onClick={() => savePreferences({ necessary: true, preferences: false, analytics: false, performance: false, marketing: false, social: false })}
            >
              Hylkää kaikki
            </ResponsiveButton>
            <ResponsiveButton
              variant="makeabooking"
              onClick={() => setShowDetails((prev) => !prev)}
            >
              Personoi
            </ResponsiveButton>
          </div>

          {/* Tarkemmat valinnat */}
          <AnimatePresence>
            {showDetails && (
              <motion.div
                key="details"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3 }}
                className="mt-4 flex flex-col items-start gap-3 max-w-md mx-auto"
              >
                {/* Pakolliset (ei muokattavissa) */}
                <label className="flex items-center gap-2 text-sm font-customFont1 dark:text-primary text-darkest dark:text-shadow-lg">
                  <input type="checkbox" checked disabled className="w-4 h-4" />
                  Pakolliset evästeet (toiminnalle tarpeelliset)
                </label>

                {/* Toiminnalliset */}
                <label className="flex items-center gap-2 text-sm font-customFont1 dark:text-primary text-darkest dark:text-shadow-lg">
                  <input
                    type="checkbox"
                    checked={preferences.preferences}
                    onChange={(e) =>
                      setPreferences({ ...preferences, preferences: e.target.checked })
                    }
                    className="w-4 h-4"
                  />
                  Toiminnalliset evästeet (asetukset ja valinnat)
                </label>

                {/* Analytiikka */}
                <label className="flex items-center gap-2 text-sm font-customFont1 dark:text-primary text-darkest dark:text-shadow-lg">
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={(e) =>
                      setPreferences({ ...preferences, analytics: e.target.checked })
                    }
                    className="w-4 h-4"
                  />
                  Analytiikkaevästeet
                </label>

                {/* Suorituskyky */}
                <label className="flex items-center gap-2 text-sm font-customFont1 dark:text-primary text-darkest dark:text-shadow-lg">
                  <input
                    type="checkbox"
                    checked={preferences.performance}
                    onChange={(e) =>
                      setPreferences({ ...preferences, performance: e.target.checked })
                    }
                    className="w-4 h-4"
                  />
                  Suorituskykyevästeet
                </label>

                {/* Markkinointi */}
                <label className="flex items-center gap-2 text-sm font-customFont1 dark:text-primary text-darkest dark:text-shadow-lg">
                  <input
                    type="checkbox"
                    checked={preferences.marketing}
                    onChange={(e) =>
                      setPreferences({ ...preferences, marketing: e.target.checked })
                    }
                    className="w-4 h-4"
                  />
                  Markkinointi-evästeet
                </label>

                {/* Sosiaalinen media */}
                <label className="flex items-center gap-2 text-sm font-customFont1 dark:text-primary text-darkest dark:text-shadow-lg">
                  <input
                    type="checkbox"
                    checked={preferences.social}
                    onChange={(e) =>
                      setPreferences({ ...preferences, social: e.target.checked })
                    }
                    className="w-4 h-4"
                  />
                  Sosiaalisen median evästeet
                </label>

                <ResponsiveButton
                  variant="makeabooking"
                  onClick={() => savePreferences(preferences)}
                >
                  Tallenna valinta
                </ResponsiveButton>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
